// MST
import { types, flow, cast } from 'mobx-state-tree'

// Interfaces
import {
  IHomeCertificationAgencyDeleteImageAttrs,
  IHomeCertificationAgencyGetAllAttrs,
  IHomeCertificationAgencyGetAttrs,
  IHomeCertificationAgencyGetImagesAttrs,
  IHomeCertificationAgencySaveAttrs,
  IHomeCertificationAgencyUpdateImageAttrs,
} from 'interfaces/back-office/content-management/home/certification-agency/home-certification-agency-view.interface'
import {
  IHomeCertificationAgencyApiGetAllResponse,
  IHomeCertificationAgencyApiGetImagesResponse,
  IHomeCertificationAgencyApiGetResponse,
  IHomeCertificationAgencyApiPaginatedResponse,
  IHomeCertificationAgencyApiSaveResponse,
  IHomeCertificationAgencyApiUpdateImageResponse,
  IHomeCertificationAgencyApiDeleteImageResponse,
} from 'interfaces/back-office/content-management/home/certification-agency/home-certification-agency-api.interface'
import {
  IHomeCertificationAgencyDetail,
  IHomeCertificationAgencyImage,
} from 'interfaces/back-office/content-management/home/certification-agency'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Lodash
import omit from 'lodash/omit'
import cloneDeep from 'lodash/cloneDeep'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Home Certification Agency Props
const homeCertificationAgencyProps = {
  number: types.maybeNull(types.number),
  id: types.maybeNull(types.number),
  bidangSertifikasiName: types.maybeNull(types.string),
  bidangSertifikasiId: types.maybeNull(types.number),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// Home Certification Agency List Model
const HomeCertificationAgencies = types
  .model('HomeCertificationAgencies')
  .props(
    withHelper_baseResponsePaginationTypes(
      types.model('HomeCertificationAgencyList').props(omit(homeCertificationAgencyProps, ['bidangSertifikasiId'])),
    ),
  )

// Home Certification Agency Detail Model
const HomeCertificationAgencyDetail = types
  .model('HomeCertificationAgencyDetail')
  .props(omit(homeCertificationAgencyProps, ['number']))

// Gallery Image Model
const HomeCertificationAgencyImage = types.model('HomeCertificationAgencyImage').props({
  id: types.maybeNull(types.number),
  imageURL: types.maybeNull(types.string),
})

const HomeCertificationAgencyStoreModel = types
  .model('HomeCertificationAgency')
  .props({
    homeCertificationAgency_isLoading: types.optional(types.boolean, false),
    homeCertificationAgency_isSaveLoading: types.optional(types.boolean, false),
    homeCertificationAgency_isImageLoading: types.optional(types.boolean, false),
    homeCertificationAgency_homeCertificationAgencies: types.optional(HomeCertificationAgencies, {}),
    homeCertificationAgency_detail: types.optional(HomeCertificationAgencyDetail, {}),
    homeCertificationAgency_images: types.optional(types.array(HomeCertificationAgencyImage), []),
  })
  .actions(self => ({
    homeCertificationAgency_SET_LOADING: (payload: boolean): void => {
      self.homeCertificationAgency_isLoading = payload
    },
    homeCertificationAgency_SET_SAVE_LOADING: (payload: boolean): void => {
      self.homeCertificationAgency_isSaveLoading = payload
    },
    homeCertificationAgency_SET_IMAGE_LOADING: (payload: boolean): void => {
      self.homeCertificationAgency_isImageLoading = payload
    },
    homeCertificationAgency_SET_HOME_CERTIFICATION_AGENCIES: (payload: IHomeCertificationAgencyApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.homeCertificationAgency_homeCertificationAgencies = cast(mapPayload)
    },
    homeCertificationAgency_SET_HOME_CERTIFICATION_AGENCY: (payload: IHomeCertificationAgencyDetail): void => {
      self.homeCertificationAgency_detail = cast(payload)
    },
    homeCertificationAgency_SET_IMAGES: (payload: IHomeCertificationAgencyImage[]): void => {
      self.homeCertificationAgency_images = cast(payload)
    },
    homeCertificationAgency_SET_NEW_IMAGE: (payload: IHomeCertificationAgencyImage): void => {
      self.homeCertificationAgency_images = cast([...self.homeCertificationAgency_images, payload])
    },
    homeCertificationAgency_UPDATE_IMAGE: (payload: IHomeCertificationAgencyImage): void => {
      self.homeCertificationAgency_images = cast(
        cloneDeep(self.homeCertificationAgency_images.map(image => (image.id === payload.id ? payload : image))),
      )
    },
    homeCertificationAgency_DELETE_IMAGE: (payload: number): void => {
      self.homeCertificationAgency_images = cast(
        cloneDeep(self.homeCertificationAgency_images.filter(image => image.id !== payload)),
      )
    },
    homeCertificationAgency_CLEAR_IMAGES: (): void => {
      self.homeCertificationAgency_images = cast([])
    },
    homeCertificationAgency_CLEAR_HOME_CERTIFICATION_AGENCIES: (): void => {
      self.homeCertificationAgency_homeCertificationAgencies = cast({})
    },
    homeCertificationAgency_CLEAR_HOME_CERTIFICATION_AGENCY: (): void => {
      self.homeCertificationAgency_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch home certification agency
     *
     * @param {IHomeCertificationAgencyGetAllAttrs} payload
     *
     * @return {any} any
     */
    homeCertificationAgency_fetchHomeCertificationAgencies: flow(function* (payload: IHomeCertificationAgencyGetAllAttrs) {
      self.homeCertificationAgency_SET_LOADING(true)

      const response: IHomeCertificationAgencyApiGetAllResponse =
        yield self.environment.homeCertificationAgencyApi.homeCertificationAgency_getHomeCertificationAgencies({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) self.homeCertificationAgency_SET_HOME_CERTIFICATION_AGENCIES(response.res.data)

      self.homeCertificationAgency_SET_LOADING(false)
    }),

    /**
     * @description Fetch certification agency
     *
     * @param {IHomeCertificationAgencyGetAttrs} payload
     *
     * @return {any} any
     */
    homeCertificationAgency_fetchHomeCertificationAgency: flow(function* (payload: IHomeCertificationAgencyGetAttrs) {
      self.homeCertificationAgency_SET_LOADING(true)

      const response: IHomeCertificationAgencyApiGetResponse =
        yield self.environment.homeCertificationAgencyApi.homeCertificationAgency_getHomeCertificationAgency({
          params: { id: payload.params.id },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        self.homeCertificationAgency_SET_HOME_CERTIFICATION_AGENCY(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.homeCertificationAgency_SET_LOADING(false)
    }),

    /**
     * @description Save certification agency
     *
     * @param {IHomeCertificationAgencySaveAttrs} payload
     *
     * @return {any} any
     */
    homeCertificationAgency_saveHomeCertificationAgency: flow(function* (payload: IHomeCertificationAgencySaveAttrs) {
      self.homeCertificationAgency_SET_SAVE_LOADING(true)

      const response: IHomeCertificationAgencyApiSaveResponse =
        yield self.environment.homeCertificationAgencyApi.homeCertificationAgency_saveHomeCertificationAgency({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.homeCertificationAgency_SET_SAVE_LOADING(false)
    }),

    /**
     * @description Fetch images
     *
     * @param {IHomeCertificationAgencyGetImagesAttrs} payload
     *
     * @return {any} any
     */
    homeCertificationAgency_fetchImages: flow(function* (payload: IHomeCertificationAgencyGetImagesAttrs) {
      self.homeCertificationAgency_SET_IMAGE_LOADING(true)

      const response: IHomeCertificationAgencyApiGetImagesResponse =
        yield self.environment.homeCertificationAgencyApi.homeCertificationAgency_getImages({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        self.homeCertificationAgency_SET_IMAGES(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.homeCertificationAgency_SET_IMAGE_LOADING(false)
    }),

    /**
     * @description Update image
     *
     * @param {IHomeCertificationAgencyUpdateImageAttrs} payload
     *
     * @return {any} any
     */
    homeCertificationAgency_updateImage: flow(function* (payload: IHomeCertificationAgencyUpdateImageAttrs) {
      self.homeCertificationAgency_SET_IMAGE_LOADING(true)

      const response: IHomeCertificationAgencyApiUpdateImageResponse =
        yield self.environment.homeCertificationAgencyApi.homeCertificationAgency_updateImage({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig, headers: { 'Content-Type': 'multipart/form-data' } },
        })

      if (response.ok) {
        self.homeCertificationAgency_UPDATE_IMAGE(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.homeCertificationAgency_SET_IMAGE_LOADING(false)
    }),

    /**
     * @description Delete image
     *
     * @param {IHomeCertificationAgencyDeleteImageAttrs} payload
     *
     * @return {any} any
     */
    homeCertificationAgency_deleteImage: flow(function* (payload: IHomeCertificationAgencyDeleteImageAttrs) {
      self.homeCertificationAgency_SET_IMAGE_LOADING(true)

      const response: IHomeCertificationAgencyApiDeleteImageResponse =
        yield self.environment.homeCertificationAgencyApi.homeCertificationAgency_deleteImage({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        self.homeCertificationAgency_DELETE_IMAGE(response.res.data.id!)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.homeCertificationAgency_SET_IMAGE_LOADING(false)
    }),
  }))

export const createHomeCertificationAgencyStoreDefaultModel = () => types.optional(HomeCertificationAgencyStoreModel, {})
